import {
  MoreOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons'
import { useState } from 'react'
import { User } from 'Components'
import { Button, Dropdown, Form, Menu, message, Modal, notification, Progress } from 'antd'
import { Link, useParams } from 'react-router-dom'
import { store } from '~/Store'
import moment from 'moment'
import MapInput from 'Components/Weaver/Input/MapInput'

const hasValidConfig = (config, required, configDefinition) => {
  const hasRequired = required.every(key => config.hasOwnProperty(key) && config[key] !== undefined)
  const hasConfDef = !_.isEmpty(configDefinition)
  return hasRequired && hasConfDef
};

const RunNow = ({ task, scheduleUid, setExpandedRowKey }) => {

  const [modalProps, setModalProps] = useState(undefined)
  const [form] = Form.useForm()
  const { explore: { plugins } } = store.getState()
  const orchestrator = plugins['weaver-orchestrator']

  const config = task?.config ?? {}
  const configDefinition = task?.functionality?.configDefinition ?? {}
  const required = configDefinition?.required ?? []

  const handleOk = () => {
    const { config } = modalProps
    const values = form.getFieldsValue()
    const updatedConfig = {}
    for (const field in values) {
      try {
        if (values[field] === undefined) {
          // unchanged
        } else if (_.isEqual(Weaver.Attribute.withVarsMap(config[field]), values[field])) {
          // fields are equal
        } else {
          updatedConfig[field] = Weaver.Attribute.updateWithVars(config[field], values[field])
        }
      } catch (e) {
        console.error(e)
        notification.error({
          message: 'Something went wrong',
          description: e.message
        })
        modalProps.promise.reject()
        setModalProps(undefined)
      }
    }
    modalProps.promise.resolve({ updatedConfig })
    setModalProps(undefined)
  };

  const handleCancel = () => {
    modalProps.promise.reject()
    setModalProps(undefined)
  };

  const debouncedAddCron = _.debounce(async () => {
    await orchestrator.runOnce({ scheduleUid })
    setExpandedRowKey(scheduleUid)
  }, 1000, { leading: true, trailing: false });

  const handleJsonWithVars = async () => {
    const jsonWithVarsFields = []
    _.map(configDefinition.properties, (val, key) => {
      if (val.subType === 'json5-with-vars') {
        if (!_.isEmpty(task.config[key])) {
          jsonWithVarsFields.push(val)
        }
      }
    })

    if (jsonWithVarsFields.length > 0) {
      const promise = new Promise((resolve, reject) => {
        setModalProps({
          config: task.config,
          jsonWithVarsFields,
          promise: {
            resolve,
            reject
          }
        })
      })

      const { updatedConfig } = await promise
      try {
        await orchestrator.updateConfiguration({
          config: {
            ...task.config,
            ...updatedConfig
          },
          scheduleUid: uid,
          user: Weaver.getInstance().currentUser().id()
        })
        setTimeout(() => task.refetch(), 500)
      } catch (e) {
        console.error(e)
        notification.error({
          message: 'Something went wrong',
          description: e.message
        })
      }
    }
  }

  return (
    <>
      <Button
        type='text'
        disabled={!hasValidConfig(config, required, configDefinition)}
        onClick={async () => {
          await handleJsonWithVars()
          debouncedAddCron()
        }}
        icon={<PlayCircleOutlined style={{ color: !hasValidConfig(config, required, configDefinition) ? 'grey' : 'green' }} />}
      />
      <Modal
        title="Inject variables"
        destroyOnClose={true}
        visible={!!Boolean(modalProps)}
        onOk={() => handleOk()}
        onCancel={() => handleCancel()}
      >
        <Form form={form}>
          {
            modalProps &&
            Object.entries(modalProps.config).map(([key, value], i) => {
              return (
                <Form.Item
                  name={key}
                  key={key}
                >
                  <MapInput
                    field={key}
                    form={form}
                    initialValue={Weaver.Attribute.withVarsMap(value)}
                  />
                </Form.Item>
              )
            })
          }
        </Form>
      </Modal>
    </>
  )
}

const ActionsMenu = ({ task }) => {

  const { crons, uid } = task;
  const { spaceId } = useParams()
  const { explore: { plugins } } = store.getState()
  const orchestrator = plugins['weaver-orchestrator']

  const disableCrons = async () => {
    const toBeDisabled = crons.filter((cron) => cron.runOnce === false)

    await new Promise.map(toBeDisabled, (cron) => {
      const updatedCronObj = {
        cronUid: cron.uid,
        runOnce: false,
        firstRun: -1,
        cron: {}
      }
      orchestrator.updateCron(updatedCronObj)
    })
  }

  crons.find((cron) => cron.value !== undefined)

  const items = [
    {
      key: `edit-${uid}`,
      label: <Link to={`/${spaceId}/pipelines/tasks/${uid}`}>Edit</Link>,
    }, {
      key: `disable-${uid}`,
      disabled: !crons.find(({ cron }) => cron.value !== undefined),
      label: <a target="_blank">Disable</a>,
      danger: true,
      onClick: async () => {
        await disableCrons()
        message.success(`Disabled crons for ${uid}`)
      }
    }
  ]

  return (
    <Menu items={items} />
  )
}

const sortingFn = (a, b) => {
  if (a < b) {
    return -1
  } else if (a > b) {
    return 1
  } else {
    return 0
  }
}

export const tableColumns = (setExpandedRowKey, setShowConfig) => {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => _.startCase(name),
      sorter: (a, b) => sortingFn(a.name, b.name)
    }, {
      title: 'Functionality',
      dataIndex: 'functionalityName',
      key: 'job',
      render: (functionalityName) => _.startCase(functionalityName),
      sorter: (a, b) => sortingFn(a.functionalityName, b.functionalityName)
    }, {
      title: 'Created (UTC)',
      dataIndex: 'created',
      key: 'created',
      render: (createdMoment) => moment.utc(createdMoment, "YYYY-MM-DDTHH:mm:ss.SSSSZ").format("ddd, DD MMM YYYY, HH:mm"),
      sorter: (a, b) => {
        const first = moment(a.created, "YYYY-MM-DDTHH:mm:ss.SSSSZ").valueOf()
        const second = moment(b.created, "YYYY-MM-DDTHH:mm:ss.SSSSZ").valueOf()
        return sortingFn(first, second)
      }
    }, {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      sorter: (a, b) => sortingFn(a.branch, b.branch)
    }, {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      render: (userId) => <User userId={userId} />
    }, {
      title: 'Run Now',
      dataIndex: '',
      key: 'runNow',
      render: (task) => {
        return (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <RunNow
              task={task}
              setShowConfig={setShowConfig}
              setExpandedRowKey={setExpandedRowKey}
              scheduleUid={task.uid}
            />
          </div>
        )
      }
    }, {
      title: 'Last Run',
      dataIndex: 'lastRun',
      key: 'lastRun',
      sorter: (a, b) => {
        if (!a.lastRun && !b.lastRun) {
          return 0;
        } else if (!a.lastRun) {
          return -1;
        } else if (!b.lastRun) {
          return 1;
        } else {
          const first = moment(a.lastRun.started, "YYYY-MM-DDTHH:mm:ss.SSSSZ").valueOf();
          const second = moment(b.lastRun.started, "YYYY-MM-DDTHH:mm:ss.SSSSZ").valueOf();
          return sortingFn(first, second);
        }
      },
      render: (lastRun) => {
        const statusMap = {
          'running': 'active',
          'pending': 'normal',
          'errored': 'exception',
          'success': 'success'
        }

        const getColor = (status) => {
          if (status === 'success') {
            return '#52c41a'
          } else if (status === 'errored') {
            return '#eb3c3c'
          }
        }

        return (
          <div style={{ width: '100%', textAlign: 'center' }}>
            {
              lastRun?.status
                ?
                <Progress
                  type="circle"
                  status={statusMap[lastRun.status]}
                  percent={lastRun.status === 'errored' ? 100 : lastRun.progress}
                  style={{ color: `${getColor(lastRun.status)}` }}
                  size="small"
                  width={25}
                />
                :
                <div />
            }
          </div>
        )
      }
    }, {
      title: 'Actions',
      dataIndex: '',
      key: 'actions',
      render: (task) =>
        <Dropdown
          overlay={<ActionsMenu task={task} />}
        >
          <MoreOutlined />
        </Dropdown>
    },
  ]
}
